import queryClient from '@/services/queryClient'

/**
 * A write that touches many rows publishes one broadcast per row, and every
 * one of them used to invalidate the same query shapes independently: opening
 * a branch refetched `network.getNetworksOrderedByLastModification` 16 times in
 * 15 seconds (KBE-806). Deduping by query key across a short window collapses a
 * burst into one invalidation per shape.
 *
 * The burst is worse than it looks because every invalidation passes
 * `cancelRefetch: false` -- an in-flight refetch is left alone and the next one
 * queues behind it, so N broadcasts cost N *sequential* round trips.
 *
 * Scheduling only defers the invalidation; it never drops one. What arrives
 * after a flush opens a new window and refetches on its own, so a burst
 * followed by a genuinely new change still re-reads for that change.
 */
const INVALIDATION_COALESCE_MS = 50

const pendingInvalidations = new Map<string, () => void>()
let flushTimer: ReturnType<typeof setTimeout> | undefined

function flushInvalidations(): void {
  flushTimer = undefined
  const runs = [...pendingInvalidations.values()]
  pendingInvalidations.clear()
  for (const run of runs) run()
}

/**
 * Runs `run` once at the end of the current coalescing window.
 *
 * Entries are deduped by `key`, so `key` must fully determine what `run` does.
 * Two calls that would invalidate different things need different keys, or one
 * of them is silently dropped -- which is why the view-specific invalidation
 * keys on its `viewId` rather than on the procedure alone.
 *
 * `run` executes at flush time, not at schedule time, so anything reading the
 * query cache to decide what to do (`invalidateNetworksListPreservingScroll`)
 * sees the state the burst actually left behind.
 */
export function scheduleInvalidation(key: string, run: () => void): void {
  pendingInvalidations.set(key, run)
  if (flushTimer !== undefined) return
  flushTimer = setTimeout(flushInvalidations, INVALIDATION_COALESCE_MS)
}

export function scheduleInvalidateQueries(queryKey: readonly unknown[]): void {
  scheduleInvalidation(JSON.stringify(queryKey), () => {
    void queryClient.invalidateQueries(
      { queryKey, refetchType: 'active' },
      { cancelRefetch: false },
    )
  })
}

/**
 * Test support. The window is module state, and `vi.useFakeTimers()` installs a
 * fresh clock without telling this module its timer handle is now dead -- so a
 * test that leaves a flush pending would otherwise leave `flushTimer` set for
 * every later test in the file, which silently stops anything from being
 * scheduled again. Call this in `beforeEach` alongside `vi.useFakeTimers()`.
 */
export function resetCoalescedInvalidation(): void {
  if (flushTimer !== undefined) clearTimeout(flushTimer)
  flushTimer = undefined
  pendingInvalidations.clear()
}
